import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, Handshake, BadgeCheck, CreditCard, CheckCheck, Store, Wallet, RotateCcw, ShieldAlert, ShoppingBag } from "lucide-react";
import { fetchNotificaciones, fetchContadorNoti, marcarNotiLeidas } from "../lib/notificaciones";

const META = {
  mensaje: { Icon: MessageSquare, color: "#2563EB", tint: "#2563EB1a" },
  oferta: { Icon: Handshake, color: "#7E22CE", tint: "#7E22CE1a" },
  verificacion: { Icon: BadgeCheck, color: "#059669", tint: "#0596691a" },
  pago: { Icon: CreditCard, color: "#D97706", tint: "#D977061a" },
  marketplace: { Icon: Store, color: "#D97706", tint: "#D977061a" },
  retiro: { Icon: Wallet, color: "#CA8A04", tint: "#CA8A041a" },
  reembolso: { Icon: RotateCcw, color: "#DC2626", tint: "#DC26261a" },
  moderacion: { Icon: ShieldAlert, color: "#B45309", tint: "#B453091a" },
  compra: { Icon: ShoppingBag, color: "#16A34A", tint: "#16A34A1a" },
  ajuste: { Icon: Wallet, color: "#0891B2", tint: "#0891B21a" },
};

const PRIO = {
  alta: { label: "Alta", color: "#DC2626", bar: "#DC2626" },
  media: { label: "Media", color: "#D97706", bar: "#F59E0B" },
  baja: { label: "Baja", color: "#64748B", bar: "#CBD5E1" },
};

function tiempoRel(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "ahora";
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

function EstadoChip({ estado }) {
  if (estado === "atendida") {
    return <span data-testid="noti-estado-atendida" className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Atendida</span>;
  }
  if (estado === "leida") {
    return <span data-testid="noti-estado-leida" className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Leída</span>;
  }
  return <span data-testid="noti-estado-nueva" className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue">Nueva</span>;
}

export default function NotificacionesBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const ref = useRef(null);

  async function refrescarContador() {
    try { setNoLeidas(await fetchContadorNoti()); } catch (_) { /* noop */ }
  }

  async function cargar() {
    try {
      const d = await fetchNotificaciones(20);
      setItems(d.items || []);
    } catch (_) { /* noop */ }
  }

  async function abrir() {
    const next = !open;
    setOpen(next);
    if (next) {
      await cargar();
      if (noLeidas > 0) {
        try { await marcarNotiLeidas(); } catch (_) { /* noop */ }
        setNoLeidas(0);
      }
    }
  }

  function onItemClick(n) {
    setOpen(false);
    if (n.enlace) navigate(n.enlace);
  }

  useEffect(() => {
    refrescarContador();
    const t = setInterval(refrescarContador, 15000);
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={abrir}
        data-testid="topbar-notifications"
        className="relative w-10 h-10 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center text-slate-700"
        aria-label="Notificaciones"
      >
        <Bell size={18} />
        {noLeidas > 0 && (
          <span data-testid="noti-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[9px] font-bold flex items-center justify-center">
            {noLeidas > 9 ? "9+" : noLeidas}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="noti-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white rounded-2xl border border-slate-100 shadow-lifted overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <p className="font-bold text-slate-900 text-sm">Notificaciones</p>
            <span className="text-[11px] text-slate-400">{items.length}</span>
          </div>
          <div className="max-h-[400px] overflow-y-auto cl-scroll-light">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Bell size={22} className="mx-auto text-slate-300" />
                <p className="mt-2 text-sm">Sin notificaciones todavía.</p>
              </div>
            ) : (
              items.map((n) => {
                const meta = META[n.tipo] || META.mensaje;
                const Icon = meta.Icon;
                const prio = PRIO[n.prioridad] || PRIO.baja;
                const atendida = n.estado === "atendida";
                return (
                  <button
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    data-testid={`noti-item-${n.tipo}`}
                    className={`relative w-full text-left flex items-start gap-3 pl-4 pr-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.leido ? "bg-brand-blue/5" : ""} ${atendida ? "opacity-70" : ""}`}
                  >
                    <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: prio.bar }} />
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: meta.tint, color: meta.color }}>
                      <Icon size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="block text-sm font-semibold text-slate-800 truncate flex-1">{n.titulo}</span>
                        <EstadoChip estado={n.estado} />
                      </span>
                      {n.texto && <span className="block text-xs text-slate-500 truncate mt-0.5">{n.texto}</span>}
                      <span className="flex items-center gap-2 mt-0.5">
                        {n.actor_nombre && <span className="text-[10px] font-medium text-slate-500 truncate">{n.actor_nombre}</span>}
                        <span className="text-[10px] text-slate-400">{tiempoRel(n.created_at)}</span>
                        {n.prioridad === "alta" && <span className="text-[9px] font-bold uppercase" style={{ color: prio.color }}>· {prio.label}</span>}
                      </span>
                    </span>
                    {!n.leido && <span className="w-2 h-2 rounded-full bg-brand-red flex-shrink-0 mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>
          <button
            onClick={() => { setOpen(false); navigate("/actividad"); }}
            className="w-full py-2.5 text-xs font-bold text-brand-blue hover:bg-slate-50 border-t border-slate-100 flex items-center justify-center gap-1"
          >
            <CheckCheck size={13} /> Ver toda la actividad
          </button>
        </div>
      )}
    </div>
  );
}
